import {
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	forwardRef,
	Input,
	Output,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorBase } from '@shared/bases/control-value-accessor.base';
import { SharedModule } from '@shared/modules/shared.module';

@Component({
	selector: 'app-input',
	imports: [SharedModule],
	templateUrl: './input.component.html',
	styleUrl: './input.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => InputComponent),
			multi: true,
		},
	],
})
export class InputComponent extends ControlValueAccessorBase<string> {
	@Input() placeholder = '';
	@Input() size: 'large' | 'medium' | 'small' | 'xsmall' = 'medium';
	@Input() disabled = false;
	@Input() error = false;
	@Input() errorMessage = '';
	@Input() icon = '';
	@Input() iconPosition: 'left' | 'right' = 'left';
	@Input() label?: string;
	@Input() helperText?: string;
	@Input() inputId = `input-${Math.random().toString(36).substring(2, 9)}`;
	@Input() type: 'text' | 'email' | 'tel' | 'password' | 'number' = 'text';
	@Input() customInputHandler?: (event: Event) => void;

	@Output() blurred = new EventEmitter<void>();
	@Output() enterPressed = new EventEmitter<void>();
	@Output() focused = new EventEmitter<void>();

	@ContentChild('suffix') suffix: TemplateRef<any> | null = null;

	@ViewChild('inputRef') inputRef!: ElementRef<HTMLInputElement>;

	constructor(private cdRef: ChangeDetectorRef) {
		super();
	}

	/**
	 * Callback triggered on native input change.
	 * Updates the internal value and notifies Angular.
	 */
	onInput(event: Event): void {
		const input = event.target as HTMLInputElement;

		// If there is a custom handler, run it first
		if (this.customInputHandler) {
			this.customInputHandler(event);
			// Update the value after the custom handler
			// (the handler may have modified input.value)
			this.value = input.value;
		} else {
			this.value = input.value;
		}
	}

	/**
	 * Allows Angular to set the disabled state of the input.
	 */
	override setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	/**
	 * Handles blur event and emits it
	 */
	onBlur(): void {
		this.markAsTouched();
		this.blurred.emit();
	}

	/**
	 * Handles focus event and emits it
	 */
	onFocus(): void {
		this.focused.emit();
	}

	/**
	 * Handles keydown event
	 */
	onKeyDown(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault(); // Prevent form submission
			this.enterPressed.emit();
		}
	}

	/**
	 * Ensures the DOM input reflects the latest value written from Angular.
	 */
	override writeValue(value: string): void {
		super.writeValue(value);
		this.cdRef.markForCheck(); // forces re-evaluation if using OnPush
		if (this.inputRef) {
			this.inputRef.nativeElement.value = value ?? '';
		}
	}
}
